<template>
  <Teleport to="body">
    <Transition name="modal">
      <div
        v-if="open"
        class="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-start sm:items-center justify-center overflow-y-auto p-3 pt-6 sm:p-4"
        :class="zClass"
        @click="handleOverlayClick"
        role="dialog"
        aria-modal="true"
        :aria-labelledby="titleId"
      >
        <div
          class="modal-content relative w-full rounded-2xl border border-white/10 bg-[#17130f] shadow-2xl overflow-hidden flex flex-col max-h-[92vh]"
          :class="sizeClasses"
          @click.stop
        >
          <div
            class="absolute inset-x-0 top-0 h-24 pointer-events-none"
            style="background: radial-gradient(120% 200% at 20% 0%, rgba(255,77,24,0.25), transparent 70%);"
          ></div>

          <div v-if="$slots.header || title" class="relative shrink-0 flex items-start justify-between gap-4 px-6 py-5 border-b border-white/10">
            <div>
              <slot name="header">
                <h3 v-if="title" :id="titleId" class="font-heading text-lg font-bold text-white">
                  {{ title }}
                </h3>
                <p v-if="description" class="text-sm text-white/50 mt-1">
                  {{ description }}
                </p>
              </slot>
            </div>
            <button
              v-if="closable"
              type="button"
              @click="close"
              class="text-white/40 hover:text-white transition-colors p-1 rounded-md hover:bg-white/10 shrink-0"
              aria-label="Fechar"
            >
              <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          <div class="modal-body relative flex-1 overflow-y-auto px-6 py-6">
            <slot />
          </div>

          <div v-if="$slots.footer" class="relative shrink-0 px-6 py-4 border-t border-white/10 bg-white/[0.02]">
            <slot name="footer" />
          </div>
        </div>
      </div>
    </Transition>
  </Teleport>
</template>

<script>
// Pilha compartilhada (nível de módulo) dos modais abertos — o Esc só fecha
// o do topo. Sem isto, um modal aberto por cima de outro (ex: documento
// legal a partir do aceite do cadastro) fechava os dois de uma vez, porque
// cada instância registrava seu próprio listener de Escape no document.
const modalStack = []
</script>

<script setup>
import { computed, watch, onBeforeUnmount } from 'vue'

const instanceId = Symbol('modal')

const props = defineProps({
  open: { type: Boolean, default: false },
  title: { type: String, default: '' },
  description: { type: String, default: '' },
  size: { type: String, default: 'md' },
  closable: { type: Boolean, default: true },
  closeOnOverlayClick: { type: Boolean, default: true },
  // Camada do overlay — só precisa ser sobrescrita por modal que abre por
  // cima de outro (ex: documentos legais a partir do aceite do cadastro).
  zClass: { type: String, default: 'z-[1100]' },
})

const emit = defineEmits(['close', 'update:open'])

const titleId = computed(() => `modal-title-${Math.random().toString(36).slice(2, 11)}`)

const sizeClasses = computed(() => ({
  sm: 'max-w-md',
  md: 'max-w-lg',
  lg: 'max-w-2xl',
  xl: 'max-w-4xl',
}[props.size] || 'max-w-lg'))

const close = () => {
  emit('update:open', false)
  emit('close')
}

const handleOverlayClick = () => {
  if (props.closeOnOverlayClick) close()
}

let escListener = null

const handleEsc = (e) => {
  if (e.key !== 'Escape' || !props.closable) return
  // Só o modal no topo da pilha responde ao Esc — os de baixo permanecem.
  if (modalStack[modalStack.length - 1] !== instanceId) return
  close()
}

const addEscListener = () => {
  if (escListener) return
  escListener = handleEsc
  document.addEventListener('keydown', escListener)
}

const removeEscListener = () => {
  if (!escListener) return
  document.removeEventListener('keydown', escListener)
  escListener = null
}

const popFromStack = () => {
  const i = modalStack.indexOf(instanceId)
  if (i !== -1) modalStack.splice(i, 1)
}

watch(() => props.open, (isOpen) => {
  if (isOpen) {
    if (!modalStack.includes(instanceId)) modalStack.push(instanceId)
    addEscListener()
  } else {
    popFromStack()
    removeEscListener()
  }
  // Mantém o scroll travado enquanto qualquer modal seguir aberto.
  document.body.style.overflow = modalStack.length ? 'hidden' : ''
})

onBeforeUnmount(() => {
  popFromStack()
  removeEscListener()
  document.body.style.overflow = modalStack.length ? 'hidden' : ''
})
</script>

<style scoped>
.modal-enter-active,
.modal-leave-active {
  transition: opacity 0.2s ease-out;
}

.modal-enter-from,
.modal-leave-to {
  opacity: 0;
}

.modal-enter-from .modal-content,
.modal-leave-to .modal-content {
  opacity: 0;
  transform: scale(0.95);
}

.modal-content {
  transition: opacity 0.2s ease-out, transform 0.2s ease-out;
}

.modal-body {
  scrollbar-width: thin;
  scrollbar-color: #ff4d18 rgba(255, 255, 255, 0.05);
}

.modal-body::-webkit-scrollbar {
  width: 8px;
}

.modal-body::-webkit-scrollbar-track {
  background: rgba(255, 255, 255, 0.05);
  border-radius: 9999px;
}

.modal-body::-webkit-scrollbar-thumb {
  background-color: #ff4d18;
  border-radius: 9999px;
  border: 2px solid #17130f;
}

.modal-body::-webkit-scrollbar-thumb:hover {
  background-color: #ff7550;
}
</style>
